import { MOBILE_FADING_ID, MOBILE_SCROLL_ID, ROOT_ID } from '../constants';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import { RefObject } from 'preact';
import { TAB_VIEW } from '../enums';
import { useDeviceState } from './useDeviceState';
import { useSticky } from './useSticky';
import { useUiContext } from '../contexts/UiContext';

export const useMobileScroll = (
  selectedTabId?: string | null,
  withTabs = true,
): [RefObject<HTMLElement>, RefObject<HTMLElement>, RefObject<HTMLDivElement>] => {
  const [distance, setDistance] = useState<number | null>(null);
  const [endOfScroll, setEndOfScroll] = useState<boolean | null>(null);

  const { isDesktop } = useDeviceState();
  const { isAmpEnabled } = useUiContext();

  const firstListRef = useRef<HTMLElement>(null);
  const secondListRef = useRef<HTMLElement>(null);
  const stickyElementRef = useRef<HTMLDivElement>(null);

  const root = document.getElementById(ROOT_ID);
  const faddingContent = root?.shadowRoot?.querySelector(`#${MOBILE_FADING_ID}`) || null;
  const scrollableContent = root?.shadowRoot?.querySelector(`#${MOBILE_SCROLL_ID}`) || null;

  const isElementSticky = useSticky(scrollableContent, 1, stickyElementRef, setDistance);
  const isMobile = !isDesktop;

  const onScroll = useCallback(() => {
    if (isMobile && scrollableContent) {
      const { scrollHeight, clientHeight, scrollTop } = scrollableContent;
      setEndOfScroll(scrollHeight - scrollTop <= clientHeight);
    }
  }, [isMobile, scrollableContent]);

  useEffect(() => {
    if (isMobile && !withTabs) {
      const listElement = firstListRef.current?.firstElementChild;

      if (listElement instanceof HTMLElement) {
        switch (endOfScroll) {
          case null:
          case true:
            listElement.style.overflow = 'auto';
            listElement.classList.remove('hide-scroll-bar');
            break;
          case false:
            listElement.style.overflow = 'hidden';
            listElement.classList.add('hide-scroll-bar');
            break;
          default:
            break;
        }
      }
    }
  }, [withTabs, endOfScroll, isMobile]);

  useEffect(() => {
    if (isMobile && scrollableContent) {
      const { scrollHeight, clientHeight, scrollTop } = scrollableContent;

      if (endOfScroll == null) {
        setEndOfScroll(scrollHeight - scrollTop <= clientHeight);
      }

      scrollableContent.addEventListener('scroll', onScroll);
    }

    return () => (isMobile && scrollableContent ? scrollableContent.removeEventListener('scroll', onScroll) : {});
  }, [endOfScroll, isMobile, onScroll, scrollableContent]);

  useLayoutEffect(() => {
    if (isMobile && withTabs && scrollableContent !== null && distance !== null) {
      const { scrollHeight, clientHeight } = scrollableContent;
      const invisibleArea = scrollHeight - clientHeight;

      let listElement: Element | null | undefined;

      if (selectedTabId === TAB_VIEW.CATEGORIES_PURPOSES && firstListRef !== null) {
        listElement = firstListRef.current?.firstElementChild;
      }

      if (selectedTabId === TAB_VIEW.SERVICES_VENDORS && secondListRef !== null) {
        listElement = secondListRef.current?.firstElementChild;
      }

      if (listElement instanceof HTMLElement && invisibleArea >= distance) {
        if (isElementSticky || endOfScroll) {
          if (!isAmpEnabled) {
            faddingContent?.classList.add('sticky-fading');
          }
          listElement.style.overflow = 'auto';
          listElement.classList.remove('hide-scroll-bar');
          scrollableContent.classList.add('hide-scroll-bar');
        } else {
          if (!isAmpEnabled) {
            faddingContent?.classList.remove('sticky-fading');
          }
          listElement.style.overflow = 'hidden';
          listElement.classList.add('hide-scroll-bar');
          scrollableContent.classList.remove('hide-scroll-bar');
        }
      }
    }
  }, [
    isAmpEnabled,
    isElementSticky,
    scrollableContent,
    distance,
    endOfScroll,
    isMobile,
    withTabs,
    selectedTabId,
    faddingContent?.classList,
  ]);

  return [firstListRef, secondListRef, stickyElementRef];
};
